import * as styles from "./FindingAnalysisStatistics.module.css";
import {colorForFindingstatus, FindingStatus, formatFindingStatus} from "../../domain/FindingStatus";
import {Popover, Progress, Tag} from "antd";
import {VisBulletLegend, VisDonut, VisSingleContainer} from "@unovis/react";
import {useEffect, useRef} from "react";

export enum AnalysisStyle {
    TAG = "TAG",
    PROGRESS = "PROGRESS",
    INLINE = "INLINE"
}

interface FindingAnalysisStatisticsProps {
    analysisStatistics?: Partial<Record<FindingStatus, number>>;
    style?: AnalysisStyle;
    className?: string;
}

export function calculateFindingAnalysisProgress(analysisStatistics: Partial<Record<FindingStatus, number>>) {
    const allFindingCount = Object.values(analysisStatistics)
        .reduce((sum, count) => sum + count, 0);

    const analysedFindingCount = Object.entries(analysisStatistics)
        .reduce((sum, [status, count]) => {
            if (status !== FindingStatus.AFFECTED && status !== FindingStatus.NOT_AFFECTED) {
                return sum;
            }

            return sum + count;
        }, 0);

    const percent = allFindingCount === 0
        ? 0
        : Math.floor((analysedFindingCount / allFindingCount) * 100);

    return {allFindingCount, analysedFindingCount, percent};
}

function FindingAnalysisStatistics(props: FindingAnalysisStatisticsProps) {
    const spanRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        spanRef.current?.getElementsByClassName("ant-progress-text")[0]?.removeAttribute("title");
    });

    if (props.analysisStatistics == null) {
        return null;
    }

    const style = props.style ?? AnalysisStyle.PROGRESS;

    const analysisStatuses = Object.keys(props.analysisStatistics);
    const {allFindingCount, analysedFindingCount, percent} = calculateFindingAnalysisProgress(props.analysisStatistics);

    if (style === AnalysisStyle.INLINE) {
        return <div className={styles['analysis-inline']}>
            <div className={styles['analysis-inline-chart']}>
                <div className={styles['analysis-inline-donut']}>
                    <VisSingleContainer height={80} data={analysisStatuses}>
                        <VisDonut radius={32} arcWidth={8}
                                  value={(d: any) => props.analysisStatistics[d]}
                                  color={d => colorForFindingstatus(d as FindingStatus)}
                                  centralLabel={`${percent}%`}/>
                    </VisSingleContainer>
                </div>
                <div className={styles['analysis-inline-legend']}>
                    {analysisStatuses.map(status => {
                        const count = props.analysisStatistics[status];

                        return <div key={status} className={styles['analysis-inline-legend-item']}>
                            <span className={styles['analysis-inline-legend-dot']} style={{background: colorForFindingstatus(status as FindingStatus)}}/>
                            <span className={styles['analysis-inline-legend-name']}>{formatFindingStatus(status as FindingStatus)}:</span>
                            <span className={styles['analysis-inline-legend-count']}>{count}</span>
                        </div>;
                    })}
                </div>
            </div>
            <div className={styles['analysis-inline-summary']}>
                {analysedFindingCount} of {allFindingCount} {allFindingCount === 1 ? 'finding' : 'findings'} analysed
            </div>
        </div>;
    }

    const popoverContent = <div className={styles['chart-container']}>
        <VisSingleContainer height={140} data={analysisStatuses}>
            <VisDonut radius={50} value={(d: any) => props.analysisStatistics[d]} color={d => colorForFindingstatus(d as FindingStatus)}/>
        </VisSingleContainer>
        <VisBulletLegend items={analysisStatuses.map(value => {
            return {
                name: `${formatFindingStatus(value as FindingStatus)} (${props.analysisStatistics[value]})`,
                color: colorForFindingstatus(value as FindingStatus)
            };
        })}/>
    </div>;

    const component = (style === AnalysisStyle.PROGRESS)
        ? <Progress className={styles.progress} type={"circle"} percent={percent} size={32}/>
        : <Tag variant={"filled"} className={styles[statusClass(percent)]}>{percent}% complete</Tag>;

    return (
        <span className={[styles['statistics'], props.className].filter(Boolean).join(" ")} ref={spanRef}>
            <Popover title={"Analysis statistics"} placement={"right"} content={popoverContent}>
                {component}
            </Popover>
        </span>
    );

    function statusClass(percent: number) {
        if (percent === 0) {
            return "status-UNKNOWN";
        }

        if (percent === 100) {
            return "status-COMPLETE";
        }

        return "status-INCOMPLETE";
    }
}

export default FindingAnalysisStatistics;
